import { useState } from 'react';

const faqs = [
  {
    question: 'In welchen Gebieten ist IGM Facility Service tätig?',
    answer: 'Wir sind hauptsächlich in Ratingen und der gesamten Region Düsseldorf/NRW tätig – einschließlich Mettmann, Erkrath, Hilden, Langenfeld und weiterer umliegender Städte. Sprechen Sie uns gerne an, wenn Sie unsicher sind, ob wir Ihren Standort abdecken.',
  },
  {
    question: 'Wie schnell ist der Schlüsseldienst im Notfall verfügbar?',
    answer: 'Unser Schlüsseldienst ist 24 Stunden am Tag, 7 Tage die Woche erreichbar. In der Regel sind wir innerhalb von 20–30 Minuten bei Ihnen vor Ort – auch nachts, an Wochenenden und Feiertagen. Rufen Sie uns einfach an: 02102 7398070.',
  },
  {
    question: 'Welche Reinigungsleistungen bieten Sie an?',
    answer: 'Wir bieten ein umfassendes Reinigungsportfolio: Unterhaltsreinigung, Grundreinigung, Büroreinigung, Fensterreinigung, Treppenhaus- und Außenreinigung sowie Umzugs- und Endreinigung. Alle Leistungen sind individuell auf Ihre Bedürfnisse anpassbar.',
  },
  {
    question: 'Wie erhalte ich ein Angebot für den Hausmeisterservice?',
    answer: 'Kontaktieren Sie uns telefonisch unter 02102 7398070 oder über unser Online-Kontaktformular. Wir vereinbaren einen kostenlosen Vor-Ort-Termin, besprechen Ihre Anforderungen und erstellen Ihnen ein unverbindliches, transparentes Angebot.',
  },
  {
    question: 'Arbeiten Sie auch für Privatpersonen oder nur für Unternehmen?',
    answer: 'Wir betreuen sowohl Privatkunden als auch Gewerbekunden, Hausverwaltungen und Wohnungsbaugesellschaften. Ob Einfamilienhaus, Mehrfamilienhaus oder Bürogebäude – wir haben die passende Lösung für jeden Bedarf.',
  },
  {
    question: 'Sind Ihre Mitarbeiter versichert und zuverlässig geprüft?',
    answer: 'Ja. Alle unsere Mitarbeiter sind fest angestellt, vollständig versichert und sorgfältig ausgewählt. Wir legen großen Wert auf Zuverlässigkeit, Diskretion und professionelles Auftreten – damit Sie sich jederzeit sicher fühlen können.',
  },
  {
    question: 'Bieten Sie auch Winterdienst an?',
    answer: 'Ja, unser Winterdienst sorgt zuverlässig für geräumte und gestreute Wege, Einfahrten und Parkplätze – auch bei starkem Schneefall. Wir bieten Saisonverträge sowie Einzeleinsätze an und sind bei Bedarf auch frühmorgens vor Ort.',
  },
  {
    question: 'Wie sind Ihre Öffnungszeiten?',
    answer: 'Unser Büro ist Montag bis Freitag von 10:00 bis 15:00 Uhr und Samstag von 12:00 bis 15:00 Uhr erreichbar. Für Notfälle – insbesondere beim Schlüsseldienst – sind wir rund um die Uhr, 24/7, für Sie da.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section className="py-16 sm:py-24 bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#0D2B4E]/5 rounded-full px-5 py-2 mb-5">
            <i className="ri-question-answer-line text-[#0D2B4E] text-lg"></i>
            <span className="text-sm font-semibold text-[#0D2B4E] tracking-wide">Häufige Fragen</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Häufig gestellte Fragen
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Hier finden Sie Antworten auf die häufigsten Fragen rund um unsere Dienstleistungen in Ratingen und Umgebung.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className={`rounded-xl border transition-all duration-300 overflow-hidden ${
                  isOpen
                    ? 'border-[#0D2B4E]/30 shadow-md bg-[#0D2B4E]/[0.03]'
                    : 'border-gray-200 bg-white hover:border-[#0D2B4E]/20 hover:shadow-sm'
                }`}
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-4 sm:py-5 text-left cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <h4 className="text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                    <a id={`faq-${i}`} className="scroll-mt-24">{faq.question}</a>
                  </h4>
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                      isOpen ? 'bg-[#0D2B4E] text-white rotate-180' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <i className="ri-arrow-down-s-line text-lg"></i>
                  </div>
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}
                >
                  <p className="px-5 sm:px-7 pb-5 sm:pb-6 text-sm sm:text-base text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10 sm:mt-14">
          <p className="text-gray-500 text-sm mb-4">Ihre Frage ist nicht dabei?</p>
          <a
            href="/kontakt"
            className="inline-flex items-center gap-2 bg-[#0D2B4E] text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-[#0a2240] transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base shadow-md"
          >
            <i className="ri-mail-send-line text-lg"></i>
            <span>Direkt anfragen</span>
          </a>
        </div>
      </div>
    </section>
  );
}
